import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { subscriptionsApi } from '@/lib/api';
import { showApiError } from '@/lib/errors/showApiError';
import { awaitSubscriptionAccess } from '@/lib/billing/awaitSubscriptionAccess';
import { CURRENT_SUBSCRIPTION_KEY } from '@/hooks/useCurrentSubscription';
import { loadRazorpayScript, type CheckoutData } from '@/hooks/useSubscriptionCheckout';
import type { Subscription } from '@/lib/api/services/subscriptions';

/** What Razorpay hands the success callback for an order payment. */
interface RazorpayOrderResponse {
  razorpay_payment_id?: string;
  razorpay_order_id?: string;
  razorpay_signature?: string;
}

/** True once the server shows a period end different from the one renewed from. */
const periodEndMoved = (sub: Subscription, previousEnd: string | null) => {
  if (!sub.current_period_end) return false;
  const now = new Date(sub.current_period_end).getTime();
  return !previousEnd || now !== new Date(previousEnd).getTime();
};

/**
 * "Renew Now" — the ONE renewal flow, shared by the top banner, the Plans page,
 * the Billing page and the expired gate. Do not re-implement it in a component.
 *
 * The server picks the plan and the price (POST /subscriptions/renew), so the
 * client never sends either. After payment the server is asked to confirm
 * (POST /subscriptions/renew/verify); the payment webhook is the backup if the
 * tab is closed. The plan only counts as renewed once the server shows the new
 * period end — never on the browser's word alone.
 *
 * One renewal at a time per TAB — the flag is module-level because the banner
 * and the Billing notice (two hook instances) can be on screen together. A
 * second click anywhere while one is open is ignored, and the server reuses
 * the same unpaid order, so a double click can't become two payments.
 */
let renewalInFlight = false;

export function useSubscriptionRenewal() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [renewing, setRenewing] = useState(false);

  const release = useCallback(() => {
    renewalInFlight = false;
    setRenewing(false);
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: CURRENT_SUBSCRIPTION_KEY }),
      queryClient.invalidateQueries({ queryKey: ['payment-history'] }),
    ]);
  }, [queryClient]);

  /** Wait until the server shows a period end other than `previousEnd`. */
  const confirmRenewed = useCallback(
    async (previousEnd: string | null, timeoutMs = 45_000) => {
      const renewed = await awaitSubscriptionAccess({
        timeoutMs,
        until: (sub) => sub.has_access !== false && periodEndMoved(sub, previousEnd),
      });
      await refresh();
      if (renewed) {
        toast({ variant: 'success', title: 'Plan renewed', description: 'Thank you! Your plan is renewed.' });
      } else {
        toast({
          title: 'Payment received',
          description: "We're still confirming it. Your plan will update in a few minutes.",
        });
      }
      return renewed;
    },
    [refresh, toast],
  );

  const startRenewal = useCallback(async () => {
    if (renewalInFlight) return;
    renewalInFlight = true;
    setRenewing(true);

    try {
      const result = await subscriptionsApi.createRenewal();
      if (result.error) {
        showApiError(
          { error: result.code || result.error, message: result.error },
          { fallback: { title: "Couldn't start renewal", description: 'Please try again in a minute.' } },
        );
        release();
        return;
      }

      const previousEnd: string | null = result.data?.renewal?.period_end ?? null;

      // The last order was already paid (webhook was still on its way) — the
      // server applied it instead of taking a second payment.
      if (result.data?.alreadyPaid) {
        await confirmRenewed(previousEnd, 15_000);
        release();
        return;
      }

      const checkoutData: CheckoutData & { notes?: Record<string, string> } = result.data?.checkoutData;
      if (!checkoutData?.order_id) {
        showApiError(null, { fallback: { title: "Couldn't start renewal", description: 'Please try again in a minute.' } });
        release();
        return;
      }

      await loadRazorpayScript();

      let paid = false;
      const razorpay = new window.Razorpay({
        key: checkoutData.key,
        name: 'WeNext',
        description: checkoutData.description || 'Plan renewal',
        order_id: checkoutData.order_id,
        amount: checkoutData.amount,
        currency: checkoutData.currency || 'INR',
        prefill: checkoutData.prefill || {},
        // Copied onto the payment, so the webhook can recognise the renewal.
        notes: checkoutData.notes || {},
        theme: { color: '#16a34a' },
        handler: async (response: RazorpayOrderResponse) => {
          paid = true;
          toast({ title: 'Payment received', description: 'Renewing your plan…' });
          try {
            if (response?.razorpay_payment_id && response?.razorpay_signature) {
              const verified = await subscriptionsApi.verifyRenewal({
                razorpayOrderId: response.razorpay_order_id || checkoutData.order_id!,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
              });
              const reason = verified.data?.reason;
              if (reason && reason !== 'processing' && !verified.data?.applied && !verified.data?.alreadyApplied) {
                // Paid but not applied (e.g. the plan was already renewed in
                // another tab). The payment is recorded; support will sort it.
                await refresh();
                toast({
                  variant: 'destructive',
                  title: 'Payment received — action needed',
                  description: 'We got your payment but could not renew automatically. Our team will fix it or refund you.',
                });
                return;
              }
            }
            // Verify failing is not "payment failed" — the webhook still renews.
            await confirmRenewed(previousEnd);
          } finally {
            release();
          }
        },
        modal: {
          // Razorpay keeps the sheet open on a failed attempt so the client
          // can retry there; closing it ends this renewal attempt.
          ondismiss: async () => {
            if (paid) return;
            // UPI can settle on the phone just after the sheet closes.
            const renewed = await awaitSubscriptionAccess({
              timeoutMs: 20_000,
              intervalMs: 3_000,
              until: (sub) => sub.has_access !== false && periodEndMoved(sub, previousEnd),
            });
            if (renewed) {
              await refresh();
              toast({ variant: 'success', title: 'Plan renewed', description: 'Thank you! Your plan is renewed.' });
            } else {
              toast({ title: 'Payment not completed', description: 'No money was taken. You can renew any time.' });
            }
            release();
          },
        },
      });

      razorpay.on('payment.failed', (response: { error?: { description?: string; reason?: string } }) => {
        toast({
          variant: 'destructive',
          title: 'Payment failed',
          description: response?.error?.description || 'Your payment did not go through. Please try again.',
        });
      });

      razorpay.open();
    } catch (error) {
      showApiError(error as Error, {
        fallback: { title: "Couldn't open payment", description: 'Please try again in a minute.' },
      });
      release();
    }
  }, [confirmRenewed, refresh, release, toast]);

  return { startRenewal, renewing };
}
